import { API, GraphQLResult } from '@aws-amplify/api';
import type { GraphQLOptions } from '@aws-amplify/api-graphql';
import { TypedDocumentString } from '../../graphql/graphql';

export default function execute<TResult, TVariables>({
  query,
  variables,
  withApiKey,
}: {
  query: TypedDocumentString<TResult, TVariables>;
  variables?: TVariables extends Record<string, never> ? undefined : TVariables;
  withApiKey?: boolean;
}) {
  const options: GraphQLOptions = {
    query: query.toString(),
    variables: variables as Record<string, unknown>,
  };

  if (withApiKey) {
    options.authMode = 'API_KEY';
  }

  return API.graphql(options) as Promise<GraphQLResult<TResult>>;
}

export const logError = (error: Error) => {
  // eslint-disable-next-line no-console
  console.error(error);
};

export const tryGetFirstError = (error: unknown): Error => {
  let typedError: Error;

  if (
    error &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray(error.errors)
  ) {
    typedError = error.errors[0] as Error;
  } else {
    typedError = error as Error;
  }

  return typedError;
};

/**
 * Amplify rejects the promise when a GraphQL response contains `errors`,
 * even if `data` is present. Recover such partial results instead of failing.
 */
export const recoverPartialResult = <TResult>(
  error: unknown,
): GraphQLResult<TResult> => {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    'errors' in error &&
    Array.isArray(error.errors)
  ) {
    const messages = (error.errors as Error[])
      .map((graphqlError) => graphqlError.message)
      .join('\n');

    logError(
      new Error(
        `[recoverPartialResult] GraphQL returned partial data with errors:\n${messages}`,
      ),
    );

    return error as GraphQLResult<TResult>;
  }

  throw error;
};
